import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import "../../styles/themes.css";
import { useTheme } from "../../contexts/ThemeContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { useAuth } from "../../contexts/AuthContext";
import { useIsMobile } from "../../App";
import Loading from "../../components/UI/Loading";
import Config from "../../config";
import logoProd from "../../assets/logoProd.png";
import logoProdLight from "../../assets/logoProdLight.png";

const APP_DOWNLOAD_URL = "https://nachmedai.onelink.me/455X/qiw6s7jm";
const APP_STORE_URL = APP_DOWNLOAD_URL;
const GOOGLE_PLAY_URL = APP_DOWNLOAD_URL;

const MarkdownLinkWithTooltip = ({ href, children, ...props }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const displayText = React.Children.toArray(children).join('');

  // If the link text is a URL (auto-linked), hide it completely
  if (displayText.startsWith('http://') || displayText.startsWith('https://')) {
    return null;
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      {...props}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      style={{
        position: "relative",
        color: "#2563eb",
        textDecoration: "none",
        fontWeight: 500,
      }}
    >
      {displayText}
      {showTooltip && (
        <span style={{
          position: "absolute",
          bottom: "100%",
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "#333",
          color: "#fff",
          padding: "4px 8px",
          borderRadius: 4,
          fontSize: 12,
          whiteSpace: "nowrap",
          zIndex: 1000,
          marginBottom: 4,
        }}>
          {href}
        </span>
      )}
    </a>
  );
};

const SharedChatScreen = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Extract shareId: from route param if available, otherwise parse from pathname
  const shareId = params.shareId || location.pathname.split('/shared/')[1];
  const { theme, isDarkMode } = useTheme();
  const { t } = useLocalization();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sharedData, setSharedData] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Show modal after 15 seconds (only for non-authenticated users)
  useEffect(() => {
    if (isAuthenticated) return;

    const timer = setTimeout(() => {
      setShowModal(true);
    }, 15000);

    return () => clearTimeout(timer);
  }, [isAuthenticated]);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;

    const fetchSharedChat = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${Config.baseUrl}share/${shareId}`);

        if (!response.ok) {
          if (response.status === 404) {
            setError("notFound");
          } else {
            setError("serverError");
          }
          return;
        }

        const data = await response.json();

        setSharedData(data);
      } catch (err) {
        console.error("Failed to fetch shared chat:", err);
        setError("networkError");
      } finally {
        setLoading(false);
      }
    };

    if (shareId) {
      fetchSharedChat();
    }
  }, [shareId, authLoading, isAuthenticated, navigate]);

  const containerStyles = {
    minHeight: "100vh",
    backgroundColor: theme.background,
    display: "flex",
    flexDirection: "column",
  };

  const headerStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: isMobile ? "center" : "space-between",
    padding: isMobile ? "4px 12px 10px" : "0 32px",
    height: isMobile ? "auto" : 60,
    borderBottom: `1px solid ${theme.border}`,
    backgroundColor: theme.surface,
    position: "relative",
    overflow: isMobile ? "hidden" : "visible",
    ...(isMobile && {
      flexDirection: "column",
      gap: 4,
    }),
  };

  const headerTitleStyles = {
    ...(isMobile
      ? {
          textAlign: "center",
          maxWidth: "100%",
          marginBottom: 12,
        }
      : {
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          maxWidth: "50%",
        }),
    color: theme.text,
    fontSize: isMobile ? 14 : 18,
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const userMessageStyles = {
    backgroundColor: theme.primary,
    color: "#fff",
    padding: "12px 16px",
    borderRadius: "16px 16px 4px 16px",
    marginBottom: 16,
    maxWidth: "85%",
    marginLeft: "auto",
  };

  const headerButtonsStyles = {
    display: "flex",
    gap: isMobile ? 8 : 12,
    alignItems: "center",
    flexShrink: 0,
    ...(isMobile && {
      width: "100%",
      justifyContent: "center",
      paddingTop: 0,
      paddingBottom: 0,
    }),
  };

  const loginButtonStyles = {
    backgroundColor: "transparent",
    color: theme.primary,
    border: `1px solid ${theme.primary}`,
    padding: "8px 16px",
    borderRadius: 8,
    fontSize: 14,
    cursor: "pointer",
    transition: "all 0.2s",
    fontWeight: 500,
    whiteSpace: "nowrap",
  };

  const registerButtonStyles = {
    backgroundColor: theme.primary,
    color: "#fff",
    border: "none",
    padding: "8px 16px",
    borderRadius: 8,
    fontSize: 14,
    cursor: "pointer",
    transition: "opacity 0.2s",
    fontWeight: 500,
    whiteSpace: "nowrap",
  };

  const modalOverlayStyles = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  };

  const modalContentStyles = {
    backgroundColor: theme.surface,
    borderRadius: 16,
    padding: 32,
    maxWidth: 450,
    width: "90%",
    textAlign: "center",
    position: "relative",
  };

  const modalCloseStyles = {
    position: "absolute",
    top: 12,
    right: 12,
    background: "none",
    border: "none",
    fontSize: 24,
    cursor: "pointer",
    color: theme.text,
    opacity: 0.5,
  };

  const logoStyles = {
    height: isMobile ? 100 : 150,
    objectFit: "contain",
    cursor: "pointer",
    flexShrink: 0,
    ...(isMobile && {
      marginBottom: -14,
      marginTop: -14,
    }),
  };

  const contentStyles = {
    flex: 1,
    maxWidth: 800,
    margin: "0 auto",
    width: "100%",
    padding: "24px",
  };

  const messageContainerStyles = {
    marginBottom: 24,
  };

  const assistantMessageStyles = {
    backgroundColor: theme.surface,
    color: theme.text,
    padding: "16px",
    borderRadius: "16px 16px 16px 4px",
    border: `1px solid ${theme.border}`,
  };

  const errorContainerStyles = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
    padding: 24,
    textAlign: "center",
  };

  const errorTitleStyles = {
    fontSize: 24,
    fontWeight: 600,
    color: theme.text,
    marginBottom: 12,
  };

  const errorDescStyles = {
    fontSize: 16,
    color: theme.textSecondary || theme.text,
    marginBottom: 24,
    opacity: 0.7,
  };

  const buttonStyles = {
    backgroundColor: theme.primary,
    color: "#fff",
    border: "none",
    padding: "12px 24px",
    borderRadius: 8,
    fontSize: 16,
    cursor: "pointer",
    transition: "opacity 0.2s",
    textDecoration: "none",
    display: "inline-block",
  };

  const storeButtonsStyles = {
    display: "flex",
    gap: 16,
    justifyContent: "center",
    marginTop: 24,
    flexWrap: "wrap",
  };

  const storeButtonStyles = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    backgroundColor: isDarkMode ? "#fff" : "#000",
    color: isDarkMode ? "#000" : "#fff",
    padding: "10px 20px",
    borderRadius: 8,
    textDecoration: "none",
    fontSize: 14,
    fontWeight: 500,
    transition: "opacity 0.2s",
  };

  const getErrorMessage = () => {
    switch (error) {
      case "notFound":
        return {
          title: t("sharedNotFound") || "Chat not found",
          description: t("sharedNotFoundDesc") || "This shared link may have expired or been removed.",
        };
      case "serverError":
        return {
          title: t("serverError") || "Server error",
          description: t("serverErrorDesc") || "Something went wrong. Please try again later.",
        };
      case "networkError":
        return {
          title: t("networkError") || "Connection error",
          description: t("networkErrorDesc") || "Please check your internet connection and try again.",
        };
      default:
        return {
          title: t("error") || "Error",
          description: t("unknownError") || "An unexpected error occurred.",
        };
    }
  };

  const AppStoreIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  );

  const GooglePlayIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
    </svg>
  );

  if (loading) {
    return (
      <div style={containerStyles}>
        <Loading fullScreen text={t("loading") || "Loading..."} />
      </div>
    );
  }

  if (error) {
    const { title, description } = getErrorMessage();
    return (
      <div style={containerStyles}>
        <header style={headerStyles}>
          <img
            src={isDarkMode ? logoProdLight : logoProd}
            alt="NachMed AI"
            style={logoStyles}
            onClick={() => navigate("/")}
          />
        </header>
        <div style={errorContainerStyles}>
          <h1 style={errorTitleStyles}>{title}</h1>
          <p style={errorDescStyles}>{description}</p>
          <button
            style={buttonStyles}
            onClick={() => navigate("/")}
            onMouseEnter={(e) => (e.target.style.opacity = "0.9")}
            onMouseLeave={(e) => (e.target.style.opacity = "1")}
          >
            {t("goToHome") || "Go to Home"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyles}>
      <header style={headerStyles}>
        <img
          src={isDarkMode ? logoProdLight : logoProd}
          alt="NachMed AI"
          style={logoStyles}
          onClick={() => navigate("/")}
        />
        {sharedData?.question && (
          <div style={headerTitleStyles}>
            {sharedData.question}
          </div>
        )}
        {!isAuthenticated && (
          <div style={headerButtonsStyles}>
            <button
              style={loginButtonStyles}
              onClick={() => navigate("/login")}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = theme.primary;
                e.target.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
                e.target.style.color = theme.primary;
              }}
            >
              {t("login") || "Увійти"}
            </button>
            <button
              style={registerButtonStyles}
              onClick={() => navigate("/register")}
              onMouseEnter={(e) => (e.target.style.opacity = "0.9")}
              onMouseLeave={(e) => (e.target.style.opacity = "1")}
            >
              {t("register") || "Реєстрація"}
            </button>
          </div>
        )}
      </header>

      <div style={contentStyles}>
        <div style={messageContainerStyles}>
          {/* User question */}
          {sharedData?.question && (
            <div style={userMessageStyles}>
              {sharedData.question}
            </div>
          )}

          {/* AI response */}
          {sharedData?.answer && (
            <div style={assistantMessageStyles}>
              {/* Stance Analysis Visualization */}
              {sharedData?.stance_analysis?.weighted_percentages && (
                <div style={{ marginBottom: 24 }}>
                  {/* Summary */}
                  {sharedData.stance_analysis.summary && (
                    <p style={{
                      fontSize: 16,
                      lineHeight: 1.5,
                      color: theme.text,
                      marginBottom: 20,
                      marginTop: 0,
                    }}>
                      {(() => {
                        const summary = sharedData.stance_analysis.summary;
                        if (!summary) return null;
                        const commaIndex = summary.indexOf(',');
                        if (commaIndex > 0 && commaIndex < 50) {
                          return (
                            <>
                              <strong style={{ fontWeight: 600 }}>{summary.slice(0, commaIndex)}</strong>
                              {summary.slice(commaIndex)}
                            </>
                          );
                        }
                        return summary;
                      })()}
                    </p>
                  )}

                  {/* Scale */}
                  {(() => {
                    const wp = sharedData.stance_analysis.weighted_percentages;
                    const total = wp.yes + wp.likely + wp.inconclusive + wp.no;
                    if (total === 0) return null;

                    const segments = [
                      { key: "yes", value: wp.yes },
                      { key: "likely", value: wp.likely },
                      { key: "inconclusive", value: wp.inconclusive },
                      { key: "no", value: wp.no },
                    ].filter(s => s.value > 0);

                    return (
                      <div className="stance-bar-container">
                        {segments.map((segment) => (
                          <div
                            key={segment.key}
                            className={`stance-bar-segment stance-${segment.key} ${segment.key === "yes" || segment.key === "no" ? "text-light" : "text-dark"}`}
                            style={{ flex: segment.value }}
                          >
                            {`${segment.value}%`}
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Legend */}
                  <details className={`stance-details ${isDarkMode ? "dark" : "light"}`}>
                    <summary className="stance-details-summary" style={{ color: theme.text }}>
                      <span className="stance-details-arrow">▶</span>
                      {t("allDetails") || "Всі деталі"}
                    </summary>
                    <div className={`stance-legend-content ${isDarkMode ? "dark" : "light"}`}>
                      {[
                        { key: "yes", labelKey: "stanceYes", label: "Так" },
                        { key: "likely", labelKey: "stanceLikely", label: "Ймовірно" },
                        { key: "inconclusive", labelKey: "stanceInconclusive", label: "Невизначено" },
                        { key: "no", labelKey: "stanceNo", label: "Ні" },
                      ].map((item) => {
                        const value = sharedData.stance_analysis.weighted_percentages[item.key];
                        if (value === 0) return null;
                        return (
                          <div key={item.key} className="stance-legend-item">
                            <div className={`stance-legend-dot stance-${item.key}`} />
                            <span className="stance-legend-label" style={{ color: theme.text, fontWeight: 600 }}>
                              {t(item.labelKey) || item.label}
                            </span>
                            <span className="stance-legend-value" style={{ color: theme.text, opacity: 0.7 }}>
                              {value}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </details>
                </div>
              )}

              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeKatex]}
                components={{
                  a: MarkdownLinkWithTooltip,
                  p: ({ children }) => (
                    <p style={{ margin: "0 0 1em 0", maxWidth: "100%", whiteSpace: "pre-wrap" }}>{children}</p>
                  ),
                  ul: ({ children }) => (
                    <ul style={{ margin: "8px 0", paddingLeft: 20 }}>{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol style={{ margin: "8px 0", paddingLeft: 20 }}>{children}</ol>
                  ),
                  li: ({ children }) => (
                    <li style={{ margin: "4px 0" }}>{children}</li>
                  ),
                  code: ({ inline, children }) =>
                    inline ? (
                      <code
                        style={{
                          backgroundColor: theme.border,
                          padding: "2px 6px",
                          borderRadius: 4,
                          fontSize: "0.9em",
                        }}
                      >
                        {children}
                      </code>
                    ) : (
                      <pre
                        style={{
                          backgroundColor: theme.border,
                          padding: 12,
                          borderRadius: 8,
                          overflow: "auto",
                        }}
                      >
                        <code>{children}</code>
                      </pre>
                    ),
                }}
              >
                {sharedData.answer}
              </ReactMarkdown>
            </div>
          )}
        </div>

      </div>

      {/* Modal popup after 15 seconds (only for non-authenticated users) */}
      {showModal && !isAuthenticated && (
        <div style={modalOverlayStyles} onClick={() => setShowModal(false)}>
          <div style={modalContentStyles} onClick={(e) => e.stopPropagation()}>
            <button
              style={modalCloseStyles}
              onClick={() => setShowModal(false)}
            >
              ×
            </button>

            <h3 style={{ color: theme.text, marginBottom: 8, fontSize: 20, fontWeight: 600 }}>
              {t("wantMoreAnswers") || "Хочете більше медичних порад?"}
            </h3>
            <p style={{ color: theme.text, opacity: 0.7, marginBottom: 20, fontSize: 15 }}>
              {t("signUpForFree") || "Зареєструйтесь безкоштовно та отримуйте персональні відповіді про здоров'я"}
            </p>

            {/* Auth buttons */}
            <div style={{ display: "flex", gap: 20, justifyContent: "center", marginBottom: 20 }}>
              <button
                style={loginButtonStyles}
                onClick={() => navigate("/login")}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme.primary;
                  e.target.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.color = theme.primary;
                }}
              >
                {t("login") || "Увійти"}
              </button>
              <button
                style={registerButtonStyles}
                onClick={() => navigate("/register")}
                onMouseEnter={(e) => (e.target.style.opacity = "0.9")}
                onMouseLeave={(e) => (e.target.style.opacity = "1")}
              >
                {t("register") || "Реєстрація"}
              </button>
            </div>

            {/* Divider */}
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              margin: "16px 0",
              color: theme.text,
              opacity: 0.5,
            }}>
              <div style={{ flex: 1, height: 1, backgroundColor: theme.border }} />
              <span style={{ fontSize: 13 }}>{t("orDownloadApp") || "або завантажте додаток"}</span>
              <div style={{ flex: 1, height: 1, backgroundColor: theme.border }} />
            </div>

            {/* Store buttons */}
            <div style={{ ...storeButtonsStyles, marginTop: 0 }}>
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={storeButtonStyles}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                <AppStoreIcon />
                <span>App Store</span>
              </a>
              <a
                href={GOOGLE_PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={storeButtonStyles}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                <GooglePlayIcon />
                <span>Google Play</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharedChatScreen;
